import { useRef, useState, useEffect } from "react";
import { t } from "i18next";
import { Button, Input, Typography, Spinner, Tooltip } from "@material-tailwind/react";
import { Table } from "../../../components/table/Table";
import { createTextSearchQuery, searchWithFilters } from "../../../services/api/search";
import { createPortal } from "react-dom";
import { PencilSquareIcon, TrashIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface ReplenishmentPartsManagerProps {
  parts: EasyTech.ReplenishmentPart[];
  onChange: (parts: EasyTech.ReplenishmentPart[]) => void;
  disabled?: boolean;
}

/**
 * Composant réutilisable pour gérer l'ajout et la suppression de pièces dans un replenishment
 */
export default function ReplenishmentPartsManager({
  parts,
  onChange,
  disabled = false,
}: ReplenishmentPartsManagerProps) {
  const [partToAdd, setPartToAdd] = useState<{ part?: EasyTech.Part, quantity: number | string }>({ quantity: "" });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingQuantity, setEditingQuantity] = useState<string>("");

  // Part search states
  const [partSearchQuery, setPartSearchQuery] = useState<string>("");
  const [partSearchResults, setPartSearchResults] = useState<EasyTech.Part[]>([]);
  const [isPartSearchLoading, setIsPartSearchLoading] = useState<boolean>(false);
  const partInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [partInputPosition, setPartInputPosition] = useState({ top: 0, left: 0, width: 0, height: 0, bottom: 0 });

  const canEditQuantity = !disabled;

  // Fermer le dropdown lorsqu'on clique à l'extérieur
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        partInputRef.current &&
        !partInputRef.current.contains(event.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target as Node))
      ) {
        if (partSearchResults.length > 0) {
          setPartSearchResults([]);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [partSearchResults]);

  /**
   * Search for parts
   */
  const searchParts = async (query: string) => {
    if (query.trim().length < 2) {
      setPartSearchResults([]);
      return;
    }

    try {
      setIsPartSearchLoading(true);
      const searchOptions = {
        queries: [createTextSearchQuery(["label", "ref"], query, "LIKE")],
        paginate: { pageSize: 10, pageNumber: 0 }
      };
      const response = await searchWithFilters<EasyTech.Part>("parts", searchOptions);
      setPartSearchResults(response.data || []);
    } catch (error) {
      console.error("Error searching parts:", error);
      setPartSearchResults([]);
    } finally {
      setIsPartSearchLoading(false);
    }
  };

  /**
   * Debounce part search
   */
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (partSearchQuery.trim().length >= 2) {
        searchParts(partSearchQuery);
      } else {
        setPartSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [partSearchQuery]);

  /**
   * Update input position for dropdown
   */
  useEffect(() => {
    if (partInputRef.current && partSearchResults.length > 0) {
      const rect = partInputRef.current.getBoundingClientRect();
      setPartInputPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom + window.scrollY
      });
    }
  }, [partSearchResults.length]);

  const handleAddPart = () => {
    if (!partToAdd.part || !partToAdd.quantity) return;

    const newPart: EasyTech.ReplenishmentPart = {
      part: partToAdd.part,
      quantity: parseFloat(String(partToAdd.quantity)) || 0
    };

    onChange([...parts, newPart]);
    setPartToAdd({ quantity: "" });
    setPartSearchQuery("");
  };

  const handleRemovePart = (index: number) => {
    onChange(parts.filter((_, i) => i !== index));
  };

  const commitQuantityChange = (index: number, value: string) => {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setEditingIndex(null);
      setEditingQuantity("");
      return;
    }

    const updatedParts = [...parts];
    updatedParts[index] = {
      ...updatedParts[index],
      quantity: parsed
    };

    onChange(updatedParts);
    setEditingIndex(null);
    setEditingQuantity("");
  };

  return (
    <div className="w-full">
      <Typography variant="h6" className="mb-3">{t("replenishment:actions:addPart")}</Typography>

      {/* Add part section */}
      <div className="mb-6 p-4 bg-blue-gray-50 rounded-lg w-full">
        <div className="space-y-3">
          <div>
            <div className="relative">
              <Input
                ref={partInputRef}
                labelProps={{ className: "hidden" }}
                placeholder={t("workorder:tabs:parts")}
                value={partToAdd.part ? `${partToAdd.part.label} (${partToAdd.part.ref})` : partSearchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setPartSearchQuery(value);
                  if (partToAdd.part) {
                    setPartToAdd({ quantity: "" });
                  }
                }}
                icon={isPartSearchLoading ? <Spinner className="w-5 h-5" /> : undefined}
                disabled={disabled}
              />

              {partSearchResults.length > 0 && createPortal(
                <div
                  ref={dropdownRef}
                  className="z-[9999] bg-white shadow-lg max-h-60 overflow-y-auto rounded-md border border-gray-200"
                  style={{
                    position: "absolute",
                    top: partInputPosition.bottom + 4,
                    left: partInputPosition.left,
                    width: partInputPosition.width,
                  }}
                >
                  {partSearchResults.map(part => {
                    const isIntegrated = parts.some(p => p.part.id === part.id);

                    return (
                    <div
                      key={part.id}
                      className={`p-2 transition-colors border-b border-gray-100 last:border-b-0 ${
                        isIntegrated 
                          ? 'cursor-not-allowed bg-white' 
                          : 'hover:bg-[#1B7AF5] hover:text-white cursor-pointer group'
                      }`}
                      onClick={() => {
                        if (isIntegrated) return;
                        setPartToAdd({ ...partToAdd, part });
                        setPartSearchQuery("");
                        setPartSearchResults([]);
                      }}
                    >
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-2">
                          <span className={`font-medium text-sm truncate max-w-[70%] ${isIntegrated ? 'text-gray-400' : 'group-hover:text-white text-gray-900'}`}>
                            {part.label}
                          </span>
                          <span className={`text-xs truncate ${isIntegrated ? 'text-gray-400' : 'group-hover:text-white/80 text-gray-500'}`}>
                            {part.ref}
                          </span>
                        </div>
                        {isIntegrated && (
                          <span className="text-red-600 text-xs mt-0.5">
                             {t("workorder:parts:error:alreadyIntegrated")}
                          </span>
                        )}
                      </div>
                    </div>
                  )})}
                </div>,
                document.body
              )}
            </div>
          </div>

          <div>
            <Input
              type="number"
              labelProps={{ className: "hidden" }}
              placeholder={t("piece:form:fields:quantity:label")}
              value={partToAdd.quantity}
              onChange={(e) => setPartToAdd({ ...partToAdd, quantity: e.target.value })}
              min="1"
              disabled={disabled}
            />
          </div>
          <Button
            onClick={handleAddPart}
            variant="filled"
            color="blue"
            className="normal-case font-thin w-full"
            disabled={!partToAdd.part || !partToAdd.quantity || disabled}
          >
            {t("replenishment:actions:addPart")}
          </Button>
        </div>
      </div>

      {/* Parts list */}
      {parts.length > 0 && (
        <div className="w-full">
          <Typography variant="h6" className="mb-3">{t("replenishment:fields.partsOrdered")} ({parts.length})</Typography>
          <Table
            tableHead={[
              { label: t("common:ref:label"), orderKey: "ref" },
              { label: t("piece:label"), orderKey: "label" },
              { label: t("piece:form:fields:quantity:label"), orderKey: "quantity" },
              { label: t("piece:form:fields:unity:label"), orderKey: "unity" },
              { label: t("common:actions"), orderKey: "actions" }
            ]}
            enableRowLinks={false}
          >
            {parts.map((part, i) => {
              const isLast = i === parts.length - 1;
              const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50 text-black";

              return (
                <tr key={`${part.part.id}-${i}`}>
                  <td className={classes}>
                    <Tooltip content={part.part.ref}>
                      <span className="text-sm truncate block max-w-[10rem]">{part.part.ref}</span>
                    </Tooltip>
                  </td>
                  <td className={classes}>
                    <Tooltip content={part.part.label}>
                      <span className="text-sm truncate block max-w-xs">{part.part.label}</span>
                    </Tooltip>
                  </td>
                  <td className={classes}>
                    {canEditQuantity && editingIndex === i ? (
                      <input
                        type="number"
                        autoFocus
                        className="w-20"
                        value={editingQuantity}
                        onChange={(e) => setEditingQuantity(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            commitQuantityChange(i, editingQuantity);
                          }
                          if (e.key === "Escape") {
                            setEditingIndex(null);
                            setEditingQuantity("");
                          }
                        }}
                      />
                    ) : (
                      <span
                        className="text-sm"
                      >
                        {part.quantity}
                      </span>
                    )}
                  </td>
                  <td className={classes}>
                    <span className="text-sm">{part.part.unity}</span>
                  </td>
                  <td className={classes + " flex justify-center items-center gap-3"} data-no-link>
                    {canEditQuantity && editingIndex === i ? (
                      <>
                        <CheckIcon
                          className="h-5 w-5 text-green-500 hover:text-green-700 cursor-pointer"
                          onClick={() => commitQuantityChange(i, editingQuantity)}
                        />
                        <XMarkIcon
                          className="h-5 w-5 text-red-500 hover:text-red-700 cursor-pointer"
                          onClick={() => {
                            setEditingIndex(null);
                            setEditingQuantity("");
                          }}
                        />
                      </>
                    ) : (
                      <>
                        {canEditQuantity && (
                          <PencilSquareIcon
                            className="h-5 w-5 hover:text-blue-500 cursor-pointer"
                            color="#8E95A2"
                            onClick={() => {
                              setEditingIndex(i);
                              setEditingQuantity(String(part.quantity));
                            }}
                          />
                        )}
                        <TrashIcon
                          className="h-5 w-5 hover:text-red-500 cursor-pointer"
                          color="#8E95A2"
                          onClick={() => handleRemovePart(i)}
                        />
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </Table>
        </div>
      )}
    </div>
  );
}
